/**
 * Check Rolls (CONTEXT.md, "Roll"): a d20 against the statistic's modifier as the sheet derives
 * it, with the roll-time additions (Multiple Attack Penalty, range penalty, volley), fortune and
 * misfortune from Rule Elements or chosen by hand, a SubstituteRoll (Assurance), and given a DC the
 * degree of success with the natural 20 and 1 adjustments and the AdjustDegreeOfSuccess shifts,
 * as Foundry's Check.roll and DegreeOfSuccess do. Everything here is a pure function of the spec,
 * the choices and the dice, so a Roll can be amended after the fact (a DC typed in, a second
 * attack chosen) without rolling again.
 */
import type { RawPredicate } from "../engine/predicate";
import { testAfterRoll } from "../engine/predicate";
import type { DegreeOfSuccessString } from "../engine/values";
import { DEGREE_OF_SUCCESS_STRINGS } from "../engine/values";
import type { DiceSupply } from "./random";

export type CheckKind = "check" | "attack-roll" | "saving-throw" | "skill-check" | "perception-check" | "flat-check" | "initiative";

/** One modifier as the breakdown lists it. */
export interface CheckLine {
  slug: string;
  label: string;
  type: string;
  value: number;
  enabled: boolean;
  source: string | null;
  sourceName: string | null;
  /** The Effect that produced this line ends once the Roll is made (Foundry: FlatModifier `removeAfterRoll`). */
  removeAfterRoll?: boolean;
  /** Set on lines added at roll time (MAP, range penalty). */
  rollTime?: boolean;
}

export interface CheckSubstitution {
  slug: string;
  label: string;
  value: number;
  required: boolean;
  effectType: "fortune" | "misfortune";
  source: string | null;
  sourceName: string | null;
  removeAfterRoll: boolean | "if-enabled";
}

export interface CheckDegreeAdjustment {
  adjustments: Partial<Record<"all" | DegreeOfSuccessString, { label: string; amount: DegreeAdjustmentAmount }>>;
  source: string | null;
  sourceName: string | null;
  /** Set when the predicate names the rolled total (a keen rune's natural 19): tested once the die is known. */
  predicate?: RawPredicate;
}

export type DegreeAdjustmentAmount = -2 | -1 | 1 | 2 | DegreeOfSuccessString;

export interface CheckNote {
  title: string | null;
  text: string;
  outcome: string[];
  source: string | null;
  sourceName: string | null;
  /** Set when the predicate names the rolled total: tested once the die is known. */
  predicate?: RawPredicate;
}

export interface RollTwiceSource {
  keep: "higher" | "lower";
  source: string | null;
  sourceName: string | null;
  removeAfterRoll: boolean;
}

/** What the sheet knows about the check before the die is rolled: serialisable, so the Roll Log keeps it. */
export interface CheckSpec {
  label: string;
  kind: CheckKind;
  /** The sheet statistic's slug, the strike's key, or null for a flat check. */
  statistic: string | null;
  /** The statistic's modifiers as the sheet shows them; the enabled ones sum to the modifier. */
  lines: CheckLine[];
  /** An Override in force replaces the lines' sum. */
  override: number | null;
  dc: number | null;
  dcLabel: string | null;
  /** Fortune or misfortune from RollTwice Rule Elements, already reconciled (both cancel). */
  rollTwice: "keep-higher" | "keep-lower" | false;
  rollTwiceSources: RollTwiceSource[];
  substitutions: CheckSubstitution[];
  /** Enabled AdjustDegreeOfSuccess adjustments. */
  degreeAdjustments: CheckDegreeAdjustment[];
  /** Enabled notes. */
  notes: CheckNote[];
  traits: string[];
  /** Strikes and spell attacks: the penalty at 1 and 2 previous attacks (already adjusted by Rule Elements). */
  map: { label: string; penalties: [number, number] } | null;
  /** Ranged strikes: the range increment, the volley distance (`volley-30`), and whether range penalties are ignored. */
  range: { increment: number | null; max: number | null; volley: number | null; ignoreRangePenalty: boolean } | null;
}

/** What the user chose for this Roll; every choice can change after the die is rolled. */
export interface CheckChoices {
  map: 0 | 1 | 2;
  /** Which range increment the target stands in (1 = the first); null when not a ranged strike. */
  rangeIncrement: number | null;
  /** Whether the target is within the volley distance. */
  volley: boolean;
  /** Fortune or misfortune chosen by hand, beyond what the Rule Elements give. */
  fortune: boolean;
  misfortune: boolean;
  /** The slug of the substitution in use (Assurance), or null. */
  substitution: string | null;
}

export const DEFAULT_CHOICES: CheckChoices = { map: 0, rangeIncrement: null, volley: false, fortune: false, misfortune: false, substitution: null };

export interface DegreeResult {
  dc: number;
  unadjusted: DegreeOfSuccessString;
  value: DegreeOfSuccessString;
  adjustment: { label: string; amount: DegreeAdjustmentAmount } | null;
}

export interface CheckResult {
  /** Every d20 rolled, in order (two under fortune or misfortune; none while a substitution stands in). */
  dice: number[];
  /** The d20 that counts: the kept die, or the substituted value. */
  die: number;
  keep: "higher" | "lower" | null;
  /** The fortune and misfortune effects in force, by name; both together cancel out. */
  fortune: string[];
  misfortune: string[];
  cancelled: boolean;
  substitution: { slug: string; label: string; value: number } | null;
  /** The lines that count, roll-time additions included. */
  lines: CheckLine[];
  modifier: number;
  total: number;
  degree: DegreeResult | null;
  natural: 20 | 1 | null;
  notes: CheckNote[];
  physical: boolean;
}

export const MAX_RANGE_INCREMENTS = 6;
/** Foundry's calculateRangePenalty: -2 per increment beyond the first, at most -12. */
export function rangePenalty(increment: number | null): number {
  if (!increment || increment <= 1) return 0;
  return Math.max((increment - 1) * -2, -12);
}

/* --------------------------------- Degree of success --------------------------------- */

const DEGREE_INDEX: Record<DegreeOfSuccessString, number> = { criticalFailure: 0, failure: 1, success: 2, criticalSuccess: 3 };

function adjustDegree(amount: DegreeAdjustmentAmount, degree: number): number {
  if (typeof amount === "string") return DEGREE_INDEX[amount];
  return Math.min(3, Math.max(0, degree + amount));
}

/** Foundry's DegreeOfSuccess: the total against the DC in steps of 10, a natural 20 one better and a natural 1 one worse, then the first matching adjustment. */
/** Foundry's post-roll options: the total, the natural die and the distance to the DC. */
export function postRollOptions(die: number, total: number, dc: number | null): string[] {
  return [`check:total:${total}`, `check:total:natural:${die}`, `check:roll:total:natural:${die}`, ...(dc !== null ? [`check:total:delta:${total - dc}`] : [])];
}

export function degreeOfSuccess(die: number, total: number, dc: number, adjustments: CheckDegreeAdjustment[] = []): DegreeResult {
  const byDie = (degree: number): number => (die === 20 ? adjustDegree(1, degree) : die === 1 ? adjustDegree(-1, degree) : degree);
  const unadjusted = total - dc >= 10 ? byDie(3) : dc - total >= 10 ? byDie(0) : total >= dc ? byDie(2) : byDie(1);
  const after = postRollOptions(die, total, dc);
  // Later adjustments override earlier ones per outcome, as Foundry merges them into one record.
  const record: CheckDegreeAdjustment["adjustments"] = {};
  for (const a of adjustments) {
    if (a.predicate && !testAfterRoll(a.predicate, after)) continue;
    for (const outcome of ["all", ...DEGREE_OF_SUCCESS_STRINGS] as const) if (a.adjustments[outcome]) record[outcome] = a.adjustments[outcome];
  }
  let adjustment: DegreeResult["adjustment"] = null;
  for (const outcome of ["all", ...DEGREE_OF_SUCCESS_STRINGS] as const) {
    const entry = record[outcome];
    if (!entry) continue;
    if (unadjusted === 3 && entry.amount === 1) continue;
    if (unadjusted === 0 && entry.amount === -1) continue;
    if (outcome === "all" || DEGREE_INDEX[outcome] === unadjusted) {
      adjustment = { label: entry.label, amount: entry.amount };
      break;
    }
  }
  const value = adjustment ? adjustDegree(adjustment.amount, unadjusted) : unadjusted;
  return { dc, unadjusted: DEGREE_OF_SUCCESS_STRINGS[unadjusted]!, value: DEGREE_OF_SUCCESS_STRINGS[value]!, adjustment };
}

export const DEGREE_LABELS: Record<DegreeOfSuccessString, string> = { criticalSuccess: "Critical success", success: "Success", failure: "Failure", criticalFailure: "Critical failure" };

/* ------------------------------------- Resolution ------------------------------------- */

/** The lines that count for the choices: the statistic's, volley switched by the choice, then MAP and range penalty. */
export function checkLines(spec: CheckSpec, choices: CheckChoices): CheckLine[] {
  const lines = spec.lines.map((l) => (l.slug === "volley" && spec.range?.volley ? { ...l, enabled: choices.volley } : { ...l }));
  if (spec.range?.volley && !lines.some((l) => l.slug === "volley")) lines.push({ slug: "volley", label: "Volley", type: "untyped", value: -2, enabled: choices.volley, source: null, sourceName: null, rollTime: true });
  if (spec.map && choices.map > 0) {
    const value = spec.map.penalties[choices.map - 1]!;
    lines.push({ slug: "multiple-attack-penalty", label: spec.map.label, type: "untyped", value, enabled: value !== 0, source: null, sourceName: null, rollTime: true });
  }
  if (spec.range && choices.rangeIncrement !== null && choices.rangeIncrement > 1) {
    const value = rangePenalty(choices.rangeIncrement);
    lines.push({ slug: "range-penalty", label: "Range Penalty", type: "untyped", value, enabled: !spec.range.ignoreRangePenalty && value !== 0, source: null, sourceName: null, rollTime: true });
  }
  return lines;
}

/** The modifier the check adds to the die: the Override or the enabled lines' sum, plus the roll-time lines. */
export function checkModifier(spec: CheckSpec, choices: CheckChoices): number {
  const lines = checkLines(spec, choices);
  if (spec.override !== null) return spec.override + lines.filter((l) => l.rollTime && l.enabled).reduce((s, l) => s + l.value, 0);
  return lines.filter((l) => l.enabled).reduce((s, l) => s + l.value, 0);
}

/** A substitution the choices select: a required one always stands in; an optional one only when chosen. */
export function selectedSubstitution(spec: CheckSpec, choices: CheckChoices): CheckSubstitution | null {
  const required = spec.substitutions.find((s) => s.required);
  if (required) return required;
  return choices.substitution ? (spec.substitutions.find((s) => s.slug === choices.substitution) ?? null) : null;
}

/**
 * Resolve a check from dice already rolled, rolling more from the supply as the choices need
 * (a second d20 once fortune is chosen). The dice list in the result is the one to keep.
 */
export function resolveCheck(spec: CheckSpec, choices: CheckChoices, dice: number[], supply: DiceSupply, physical = false): CheckResult {
  const substitution = selectedSubstitution(spec, choices);
  const fortune: string[] = [];
  const misfortune: string[] = [];
  for (const s of spec.rollTwiceSources) (s.keep === "higher" ? fortune : misfortune).push(s.sourceName ?? "a Rule Element");
  if (spec.rollTwiceSources.length === 0 && spec.rollTwice) (spec.rollTwice === "keep-higher" ? fortune : misfortune).push("a Rule Element");
  if (substitution) (substitution.effectType === "fortune" ? fortune : misfortune).push(substitution.label);
  if (choices.fortune) fortune.push("chosen");
  if (choices.misfortune) misfortune.push("chosen");
  const cancelled = fortune.length > 0 && misfortune.length > 0;
  const standIn = cancelled ? null : substitution;
  const keep: "higher" | "lower" | null = cancelled || standIn ? null : fortune.length > 0 ? "higher" : misfortune.length > 0 ? "lower" : null;
  const needed = standIn ? 0 : keep ? 2 : 1;
  const rolled = [...dice];
  while (rolled.length < needed) rolled.push(supply(20));
  const die = standIn ? standIn.value : keep === "higher" ? Math.max(rolled[0]!, rolled[1]!) : keep === "lower" ? Math.min(rolled[0]!, rolled[1]!) : rolled[0]!;
  const lines = checkLines(spec, choices);
  const modifier = checkModifier(spec, choices);
  const total = die + modifier;
  const degree = spec.dc !== null ? degreeOfSuccess(die, total, spec.dc, spec.degreeAdjustments) : null;
  const outcome = degree?.value ?? null;
  const after = postRollOptions(die, total, spec.dc);
  const notes = spec.notes.filter((n) => (!n.predicate || testAfterRoll(n.predicate, after)) && (spec.dc === null || n.outcome.length === 0 || (outcome !== null && n.outcome.includes(outcome))));
  return {
    dice: rolled,
    die,
    keep,
    fortune,
    misfortune,
    cancelled,
    substitution: standIn ? { slug: standIn.slug, label: standIn.label, value: standIn.value } : null,
    lines,
    modifier,
    total,
    degree,
    natural: !standIn && die === 20 ? 20 : !standIn && die === 1 ? 1 : null,
    notes,
    physical,
  };
}

/** A fresh Roll: no dice yet, the supply rolls what the choices need. */
export function rollCheck(spec: CheckSpec, choices: CheckChoices, supply: DiceSupply, physical = false): CheckResult {
  return resolveCheck(spec, choices, [], supply, physical);
}

/** Whether a Hero Point may reroll this result: not already a reroll, and no fortune or misfortune in force (Foundry: CheckRoll#isRerollable). */
export function isRerollable(result: CheckResult, rerollOf: string | null): boolean {
  return rerollOf === null && result.keep === null && result.substitution === null;
}

/** The Effects a Roll ends once made: RollTwice, SubstituteRoll and FlatModifier Rule Elements with `removeAfterRoll`. */
export function effectsEndedByRoll(spec: CheckSpec, result: CheckResult): { source: string; sourceName: string | null }[] {
  const ended = new Map<string, string | null>();
  for (const s of spec.rollTwiceSources) if (s.removeAfterRoll && s.source && !result.cancelled) ended.set(s.source, s.sourceName);
  for (const s of spec.substitutions) {
    const used = result.substitution?.slug === s.slug;
    if (s.source && (s.removeAfterRoll === true || (s.removeAfterRoll === "if-enabled" && used))) ended.set(s.source, s.sourceName);
  }
  for (const l of result.lines) if (l.removeAfterRoll && l.enabled && l.source) ended.set(l.source, l.sourceName);
  return [...ended].map(([source, sourceName]) => ({ source, sourceName }));
}

export function signed(n: number): string {
  return n < 0 ? String(n) : `+${n}`;
}

/** "17 + 7 = 24 vs DC 20: success" */
export function describeCheck(result: CheckResult): string {
  const dice = result.dice.length === 2 ? `${result.dice[0]}/${result.dice[1]} keep ${result.keep === "higher" ? "higher" : "lower"} = ${result.die}` : result.substitution ? `${result.substitution.label} ${result.die}` : String(result.die);
  const head = `${dice} ${signed(result.modifier)} = ${result.total}`;
  if (!result.degree) return head;
  return `${head} vs DC ${result.degree.dc}: ${DEGREE_LABELS[result.degree.value].toLowerCase()}`;
}
